import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

function currencySymbol(code) {
  return CURRENCY_SYMBOLS[code] || code || "€";
}

function formatAmount(value, currency) {
  return `${currencySymbol(currency)}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;
}

export function generatePayslipPdf({ formData, calculation, grossEarnings, logoDataUrl }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const currency = formData.employer.currency;

  let cursorY = margin;

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", margin, cursorY, 60, 60);
    } catch (err) {
      console.warn("Could not embed logo:", err);
    }
  }

  const textStartX = logoDataUrl ? margin + 75 : margin;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(formData.employer.name || "Employer name not provided", textStartX, cursorY + 20);

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Payslip", textStartX, cursorY + 38);

  const periodLabel = `${formData.payPeriod.start || "—"}  to  ${formData.payPeriod.end || "—"}`;
  doc.setFontSize(10);
  doc.text(`Pay period: ${periodLabel.slice(8, 10)} - ${periodLabel.slice(5, 7)} - ${periodLabel.slice(0, 4)} to ${formData.payPeriod.end.slice(8, 10)} - ${formData.payPeriod.end.slice(5, 7)} - ${formData.payPeriod.end.slice(0, 4)}`, pageWidth - margin, cursorY + 20, { align: "right" });
  doc.text(`Payment date: ${formData.payPeriod.paymentDate.slice(8, 10) || "—"} - ${formData.payPeriod.paymentDate.slice(5, 7)} - ${formData.payPeriod.paymentDate.slice(0, 4)}`, pageWidth - margin, cursorY + 34, { align: "right" });

  cursorY += 90;
  doc.setDrawColor(210);
  doc.line(margin, cursorY, pageWidth - margin, cursorY);
  cursorY += 20;

  // Employer / employee info
  autoTable(doc, {
    startY: cursorY,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 100 }, 2: { fontStyle: "bold", cellWidth: 100 } },
    body: [
      ["Employer", formData.employer.name || "—", "Employee", formData.employee.name || "—"],
      ["Tax ID", formData.employer.taxId || "—", "Employee ID", formData.employee.employeeId || "—"],
      ["Employment type", formData.employer.employmentType || "—", "SI number", formData.employee.siNumber || "—"],
      ["Payment Method", formData.employee.paymentMethod || "-", "Position", formData.employee.position || "—"],
    ],
  });

  cursorY = doc.lastAutoTable.finalY + 20;

  // Earnings
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Earnings", margin, cursorY);
  cursorY += 8;

  const earningsRows = formData.earnings
    .filter((l) => parseFloat(l.amount) > 0)
    .map((l) => [l.label, formatAmount(l.amount, currency)]);

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", "Amount"]],
    body: earningsRows.length ? earningsRows : [["—", formatAmount(0, currency)]],
    foot: [["Gross earnings", formatAmount(grossEarnings, currency)]],
    theme: "striped",
    headStyles: { fillColor: [13, 27, 51] },
    footStyles: { fillColor: [240, 241, 243], textColor: [13, 27, 51], fontStyle: "bold" },
    styles: { fontSize: 10 },
    didParseCell: function (data) {
      if (data.column.index === 1) {
        data.cell.styles.halign = "right";
      }
    },
    margin: { left: margin, right: margin },
  });

  cursorY = doc.lastAutoTable.finalY + 20;

  // Deductions
  doc.setFontSize(12);
  doc.text("Deductions", margin, cursorY);
  cursorY += 8;

  const otherDeductionRows = formData.otherDeductions
    .filter((l) => parseFloat(l.amount) > 0)
    .map((l) => [l.label, formatAmount(l.amount, currency)]);

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", "Amount"]],
    body: [
      ["Income tax", formatAmount(calculation.incomeTax, currency)],
      ["Social insurance (8.8%)", formatAmount(calculation.socialInsurance.employee, currency)],
      ["GESY / GHS contribution (2.65%)", formatAmount(calculation.gesy.employee, currency)],
      ...otherDeductionRows,
    ],
    foot: [["Total deductions", formatAmount(calculation.totalDeductions, currency)]],
    theme: "striped",
    headStyles: { fillColor: [13, 27, 51] },
    footStyles: { fillColor: [240, 241, 243], textColor: [13, 27, 51], fontStyle: "bold" },
    styles: { fontSize: 10 },
    didParseCell: function (data) {
      if (data.column.index === 1) {
        data.cell.styles.halign = "right";
      }
    },
    margin: { left: margin, right: margin },
  });

  cursorY = doc.lastAutoTable.finalY + 30;

  // Net pay box
  doc.setFillColor(13, 27, 51);
  doc.roundedRect(margin, cursorY, pageWidth - margin * 2, 50, 6, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.text("Net payable", margin + 16, cursorY + 20);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(formatAmount(calculation.netPay, currency), pageWidth - margin - 16, cursorY + 32, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");

  cursorY += 80;

  if (cursorY > 650) {
    doc.addPage();
    cursorY = margin;
  }

  // Employer contributions (reference only)
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Employer contributions (reference only — not deducted from net pay)", margin, cursorY);
  cursorY += 8;

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", "Amount"]],
    body: [
      ["Social insurance (8.8%)", formatAmount(calculation.socialInsurance.employer, currency)],
      ["GESY contribution (2.9%)", formatAmount(calculation.gesy.employer, currency)],
      ["Redundancy fund (1.2%)", formatAmount(calculation.employerExtras.redundancyFund, currency)],
      ["Human resource development (0.5%)", formatAmount(calculation.employerExtras.humanResourceDevelopment, currency)],
      ["Social cohesion fund (2%)", formatAmount(calculation.employerExtras.socialCohesionFund, currency)],
    ],
    foot: [  ["Total Contributions", formatAmount(calculation.totalEmployerContributions, currency)]],
    theme: "striped",
    headStyles: { fillColor: [90, 96, 110] },
    footStyles: { fillColor: [240, 241, 243], textColor: [13, 27, 51], fontStyle: "bold" },
    styles: { fontSize: 9 },
    didParseCell: function (data) {
      if (data.column.index === 1) {
        data.cell.styles.halign = "right";
      }
    },
    margin: { left: margin, right: margin },
  });

  cursorY = doc.lastAutoTable.finalY + 20;

  if (formData.notes) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Notes", margin, cursorY);
    cursorY += 16;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(formData.notes, pageWidth - margin * 2);
    doc.text(noteLines, margin, cursorY);
  }

  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "Generated using 2026 Cyprus statutory rates. For informational purposes only — verify with the Cyprus Tax Department and Social Insurance Services.",
    margin,
    doc.internal.pageSize.getHeight() - 30,
    { maxWidth: pageWidth - margin * 2 }
  );

  const safeName = (formData.employee.name || "employee").replace(/\s+/g, "-").toLowerCase();
  const safeDate = formData.payPeriod.paymentDate || "draft";
  doc.save(`payslip-${safeName}-${safeDate}.pdf`);
}